import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Banner } from "@rentiohq/web-shared/dist/components";
import { uniq } from "lodash";
import { Money } from "../../../components/Money";
import { formatMoney } from "../../../moneyText.utils";
import { IPayShare } from "./SettlementForm.utils";

interface IPaymentStepBannerProps {
  shares: IPayShare[];
  // The per-tenant flow: which payment of how many this one is.
  step?: { index: number; total: number };
}

// Who receives the request: the payer, not every name on the lease.
const payerLabel = (share: IPayShare) => {
  const name = share.payerName ?? share.tenantName;
  return share.unitLabel ? `${name} · ${share.unitLabel}` : name;
};

const descriptionText = (description: string) =>
  getLocalizedText("settlement.payments.banner_description", { description });

// The payment step's one info banner (Lewis, 2026-10-19: one banner for who
// pays what, not one per question): who gets a payment order, for how much
// and under which description, and with several tenants each one on a line
// of their own. The payment form's own "Who pays what" is left out
// (hideCostRecap): this says the same on every step.
export const PaymentStepBanner = ({
  shares,
  step,
}: IPaymentStepBannerProps) => {
  const [first] = shares;
  if (!first) return null;

  const total = shares.reduce((sum, share) => sum + share.amount, 0);
  // Each payer's description is in their own language: one for all of
  // them goes on the first line, else each line has its own.
  const descriptions = uniq(shares.map(share => share.description));
  const sharedDescription =
    descriptions.length === 1 ? descriptions[0] : undefined;

  const getHeadline = () => {
    if (step) {
      return getLocalizedText("settlement.payments.step_of", {
        index: String(step.index),
        total: String(step.total),
        tenant: payerLabel(first),
        amount: formatMoney(total),
      });
    }
    if (shares.length === 1) {
      return getLocalizedText("settlement.payments.bulk_one", {
        tenant: payerLabel(first),
        amount: formatMoney(total),
      });
    }
    return getLocalizedText("settlement.payments.bulk_many", {
      count: String(shares.length),
      amount: formatMoney(total),
    });
  };
  const headline = getHeadline();

  return (
    <Banner variation="info" hasDismiss={false}>
      <div>
        {sharedDescription
          ? `${headline} · ${descriptionText(sharedDescription)}`
          : headline}
      </div>
      {shares.length > 1 && (
        <div style={{ marginTop: 4 }}>
          {shares.map(share => (
            <div key={share.contractId}>
              {payerLabel(share)} <Money amount={share.amount} size="inherit" />
              {!sharedDescription && ` · ${descriptionText(share.description)}`}
            </div>
          ))}
        </div>
      )}
    </Banner>
  );
};
